import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownRight, ExternalLink, Link2, Repeat, Trash2, X } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select } from './ui/select';
import { Button } from './ui/button';
import FieldLabel from './FieldLabel';
import RequestorField from './RequestorField';
import DatePicker from './DatePicker';
import MessageThread from './MessageThread';
import UserAvatar from './UserAvatar';
import SubtaskList from './SubtaskList';
import ValidationDialog from './ValidationDialog';
import { TYPES, STATUSES, PRIORITIES, DEPARTMENTS, RECURRENCES, RECURRENCE_MAP } from '../lib/constants';

const emptyForm = (status) => ({
  title: '',
  description: '',
  type: 'other',
  status: status || 'new',
  priority: 'medium',
  department: 'general',
  due_date: '',
  recurrence: 'none',
  estimateHours: '',
  requestorId: '',
  assigneeIds: [],
});

export default function TicketModal() {
  const {
    modalOpen, editing, defaultStatus, members, myId, tickets,
    saveTicket, removeTicket, createExternalMember, requestValidation,
    addAttachments, removeAttachment, closeModal, openEditTicket,
  } = useTickets();
  // Picking "Validation" in the status dropdown routes through the same note +
  // screenshots dialog as the board drop, so the Slack DM always carries a note.
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState(emptyForm('new'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  // New tickets have no id to attach to yet, so buffer the chosen images here
  // and upload them right after the ticket is created. Each item is { file, url }
  // where url is a local preview (object URL we revoke when it's dropped/reset).
  const [pending, setPending] = useState([]);
  // Subtasks typed while creating a ticket: created right after the parent
  // exists (same idea as the buffered screenshots).
  const [pendingSubs, setPendingSubs] = useState([]);
  const [subTitle, setSubTitle] = useState('');
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const handleShare = async () => {
    if (!editing) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/tickets/${editing.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };
  const openDetail = () => { if (editing) { closeModal(); navigate(`/tickets/${editing.id}`); } };

  const assignableMembers = members.filter((m) => m.is_assignable);

  useEffect(() => {
    if (!modalOpen) return;
    setError('');
    setSaving(false);
    setAttachments(editing?.attachments || []);
    setPending((prev) => { prev.forEach((p) => URL.revokeObjectURL(p.url)); return []; });
    setPendingSubs([]);
    setSubTitle('');
    setValidating(false);
    if (editing) {
      setForm({
        title: editing.title || '',
        description: editing.description || '',
        type: editing.type,
        status: editing.status,
        priority: editing.priority,
        department: editing.department || 'general',
        due_date: editing.due_date || '',
        recurrence: editing.recurrence || 'none',
        estimateHours: editing.estimate_hours ?? '',
        requestorId: editing.requestor ? String(editing.requestor.id) : '',
        assigneeIds: (editing.assignees || []).map((a) => a.id),
      });
    } else {
      setForm(emptyForm(defaultStatus));
    }
  }, [modalOpen, editing, defaultStatus]);

  if (!modalOpen) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description,
    type: form.type,
    status: form.status,
    priority: form.priority,
    department: form.department,
    due_date: form.due_date || null,
    recurrence: form.recurrence,
    estimate_hours: form.estimateHours === '' ? null : Number(form.estimateHours),
    requestor_id: form.requestorId ? Number(form.requestorId) : null,
    assignee_ids: form.assigneeIds,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Please enter a title.');
      return;
    }
    if (editing && form.status === 'validation' && editing.status !== 'validation') {
      setValidating(true);  // confirmed in handleValidate
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await saveTicket(buildPayload(), editing?.id);
      // Newly-created ticket: now it has an id, upload the buffered screenshots.
      // If this fails the ticket still exists, so log rather than block the close
      // (re-submitting would create a duplicate).
      if (!editing && pending.length) {
        try {
          await addAttachments(saved.id, pending.map((p) => p.file));
        } catch (err) {
          console.error('Ticket created, but its screenshots failed to upload', err);
        }
      }
      if (!editing && pendingSubs.length) {
        for (const t of pendingSubs) {
          try {
            await saveTicket({
              title: t, status: 'todo', parent_id: saved.id, type: saved.type, priority: saved.priority,
              department: saved.department, requestor_id: saved.requestor?.id ?? null, assignee_ids: [],
            });
          } catch (err) {
            console.error('Ticket created, but a subtask failed', err);
          }
        }
      }
      closeModal();
    } catch (err) {
      console.error(err);
      setError('Could not save the ticket. Please try again.');
      setSaving(false);
    }
  };

  // Save the other edits with the status left as it was, then do the move
  // through the validation endpoint (note → thread + Slack, screenshots →
  // attachments + Slack).
  const handleValidate = async ({ message, images }) => {
    setSaving(true);
    setError('');
    try {
      await saveTicket({ ...buildPayload(), status: editing.status }, editing.id);
      await requestValidation(editing.id, { message, images });
      setValidating(false);
      closeModal();
    } catch (err) {
      console.error(err);
      setError('Could not move the ticket to validation. Please try again.');
      setValidating(false);
      setSaving(false);
    }
  };

  const toggleAssignee = (id) => setForm((f) => ({
    ...f,
    assigneeIds: f.assigneeIds.includes(id) ? f.assigneeIds.filter((x) => x !== id) : [...f.assigneeIds, id],
  }));

  const takeIt = () => setForm((f) => (
    f.assigneeIds.includes(myId) ? f : { ...f, assigneeIds: [...f.assigneeIds, myId] }
  ));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    // New ticket: no id to attach to yet, so buffer locally and upload right
    // after it's created (see handleSubmit).
    if (!editing) {
      setPending((prev) => [...prev, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const saved = await addAttachments(editing.id, files);
      setAttachments(saved.attachments || []);
    } catch (err) {
      console.error(err);
      setError('Could not upload the screenshot.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (attId) => {
    try {
      await removeAttachment(editing.id, attId);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemovePending = (idx) => {
    setPending((prev) => {
      URL.revokeObjectURL(prev[idx]?.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const subtaskCount = editing ? tickets.filter((t) => t.parent_id === editing.id).length : 0;
  // The live record (subtask changes update the store while the modal is open).
  const liveEditing = editing ? (tickets.find((t) => t.id === editing.id) || editing) : null;
  const parentTicket = editing?.parent ? tickets.find((t) => t.id === editing.parent.id) : null;

  const handleDelete = async () => {
    if (!editing) return;
    const what = subtaskCount ? `this ticket and its ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}` : 'this ticket';
    if (!window.confirm(`Delete ${what}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await removeTicket(editing.id);
      closeModal();
    } catch (err) {
      console.error(err);
      setError('Could not delete the ticket.');
      setSaving(false);
    }
  };

  // Neighbours in a recurring series (the occurrence this one was spawned
  // from, and the one spawned when it was done), so they can be opened directly.
  const prevOccurrence = editing?.recurred_from_id
    ? tickets.find((t) => t.id === editing.recurred_from_id) : null;
  const nextOccurrence = editing ? tickets.find((t) => t.recurred_from_id === editing.id) : null;
  const jumpTo = (t) => { if (t) openEditTicket(t); };

  // Unify saved attachments (edit mode) and buffered local previews (create mode)
  // so the thumbnails render the same way in both.
  const shots = editing
    ? attachments.map((a) => ({
        key: `a${a.id}`, url: a.url, name: a.original_name, href: a.url,
        onRemove: () => handleRemoveAttachment(a.id),
      }))
    : pending.map((p, i) => ({
        key: p.url, url: p.url, name: p.file.name, href: null,
        onRemove: () => handleRemovePending(i),
      }));

  return (
    <Dialog open={modalOpen} onClose={validating ? undefined : closeModal} className="max-w-4xl">
      <form onSubmit={handleSubmit}>
        <DialogHeader title={editing ? 'edit-ticket' : 'new-ticket'} onClose={closeModal} />
        <DialogBody className="max-h-[85vh] space-y-4 overflow-y-auto">
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          {/* One column: title, then the classifiers in a row, people, and a
              full-width description. Blocks below are separated by rules. */}
          <div className="space-y-1.5">
            <FieldLabel htmlFor="t-title">title</FieldLabel>
            <Input id="t-title" value={form.title} onChange={set('title')} placeholder="What needs to be done?" autoFocus />
            {editing?.parent ? (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <CornerDownRight className="h-3 w-3" /> subtask of
                <button
                  type="button"
                  onClick={() => { if (parentTicket) openEditTicket(parentTicket); }}
                  className="truncate text-primary hover:underline"
                  disabled={!parentTicket}
                >
                  #{editing.parent.id} {editing.parent.title}
                </button>
              </p>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="t-type">type</FieldLabel>
              <Select id="t-type" value={form.type} onChange={set('type')}>
                {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="t-status">status</FieldLabel>
              <Select id="t-status" value={form.status} onChange={set('status')}>
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="t-priority">priority</FieldLabel>
              <Select id="t-priority" value={form.priority} onChange={set('priority')}>
                {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="t-dept">department</FieldLabel>
              <Select id="t-dept" value={form.department} onChange={set('department')}>
                {DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </Select>
            </div>
          </div>

          <div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <FieldLabel>requestor</FieldLabel>
              <RequestorField
                members={members}
                value={form.requestorId}
                onChange={(v) => setForm((f) => ({ ...f, requestorId: v }))}
                onCreateExternal={createExternalMember}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <FieldLabel>assignees</FieldLabel>
                {myId && assignableMembers.some((m) => m.id === myId) && !form.assigneeIds.includes(myId) ? (
                  <button type="button" onClick={takeIt} className="text-xs text-primary hover:underline">+ take it</button>
                ) : null}
              </div>
              {assignableMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground">No assignable team members yet.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {assignableMembers.map((m) => {
                    const on = form.assigneeIds.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleAssignee(m.id)}
                        className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition ${
                          on
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        <UserAvatar user={m} className="h-3.5 w-3.5 rounded-sm" />
                        {on ? '✓ ' : ''}{m.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <FieldLabel htmlFor="t-desc">description</FieldLabel>
            <Textarea id="t-desc" value={form.description} onChange={set('description')} placeholder="Details, context, links…" className="min-h-[140px]" />
          </div>

          {/* Subtasks: a live checklist for existing tickets; a buffered list
              of titles for new ones. Subtasks themselves don't nest further. */}
          {editing && !editing.parent ? (
            <div className="border-t border-border pt-4">
              <SubtaskList parent={liveEditing} />
            </div>
          ) : null}
          {!editing ? (
            <div className="border-t border-border pt-4">
              <h2 className="mb-2 font-mono text-sm text-muted-foreground">{'// subtasks'}{pendingSubs.length ? ` (${pendingSubs.length})` : ''}</h2>
              {pendingSubs.length ? (
                <ul className="mb-2 space-y-0.5">
                  {pendingSubs.map((t, i) => (
                    <li key={`${t}-${i}`} className="group flex items-center gap-2 rounded-md px-1 py-1 text-sm hover:bg-accent/40">
                      <span className="h-4 w-4 shrink-0 rounded-sm border border-border" />
                      <span className="min-w-0 flex-1 truncate">{t}</span>
                      <button type="button" onClick={() => setPendingSubs((p) => p.filter((_, j) => j !== i))} className="text-muted-foreground opacity-0 transition hover:text-destructive group-hover:opacity-100" aria-label={`remove ${t}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Input
                value={subTitle}
                onChange={(e) => setSubTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (subTitle.trim()) { setPendingSubs((p) => [...p, subTitle.trim()]); setSubTitle(''); }
                }}
                placeholder="Add a subtask and press Enter — they're created with the ticket; assign them afterwards"
                className="h-8 text-sm"
              />
            </div>
          ) : null}

          <div className="border-t border-border pt-4">
            <h2 className="mb-3 font-mono text-sm text-muted-foreground">{'// planning'}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="t-est">estimate_hours</FieldLabel>
                <Input id="t-est" type="number" min="0" step="0.5" value={form.estimateHours} onChange={set('estimateHours')} placeholder="e.g. 4" />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="t-due">due_date</FieldLabel>
                <DatePicker id="t-due" value={form.due_date} onChange={(v) => setForm((f) => ({ ...f, due_date: v }))} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="t-repeat">repeats</FieldLabel>
                <Select id="t-repeat" value={form.recurrence} onChange={set('recurrence')}>
                  {RECURRENCES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </Select>
              </div>
            </div>
            {form.recurrence !== 'none' ? (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Repeat className="mt-0.5 h-3 w-3 shrink-0" />
                <span>
                  {RECURRENCE_MAP[form.recurrence]?.label.toLowerCase()} — when this ticket is marked done,
                  the next occurrence is created in To do with the due date moved forward.
                  {prevOccurrence ? (
                    <>
                      {' '}
                      <button type="button" onClick={() => jumpTo(prevOccurrence)} className="text-primary hover:underline">
                        previous #{prevOccurrence.id}
                      </button>
                    </>
                  ) : null}
                  {nextOccurrence ? (
                    <>
                      {' '}
                      <button type="button" onClick={() => jumpTo(nextOccurrence)} className="text-primary hover:underline">
                        next #{nextOccurrence.id}
                      </button>
                    </>
                  ) : null}
                </span>
              </p>
            ) : null}
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-mono text-sm text-muted-foreground">{'// screenshots'}{shots.length ? ` (${shots.length})` : ''}</h2>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-xs text-primary hover:underline disabled:opacity-50"
                disabled={uploading}
              >
                {uploading ? 'uploading…' : '+ add'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
            </div>
            {shots.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {shots.map((s) => {
                  const thumb = <img src={s.url} alt={s.name} className="h-full w-full object-cover" />;
                  return (
                    <div key={s.key} className="group relative h-16 w-16 overflow-hidden rounded-md border border-border">
                      {s.href ? <a href={s.href} target="_blank" rel="noreferrer">{thumb}</a> : thumb}
                      <button
                        type="button"
                        onClick={s.onRemove}
                        className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                        aria-label="Remove screenshot"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {editing ? 'No screenshots yet.' : "Add screenshots — they'll upload when you create the ticket."}
              </p>
            )}
          </div>

          {/* Existing tickets get their message thread right in the modal, so
              updates can be posted from the board without opening the detail
              page. New tickets have no id (and no thread) yet. */}
          {editing ? (
            <div className="border-t border-border pt-4">
              <MessageThread ticketId={editing.id} />
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter className="justify-between">
          <div className="flex items-center gap-1">
            {editing ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={handleShare} title="Copy a shareable link">
                  <Link2 className="mr-1 h-3.5 w-3.5" /> {copied ? 'copied' : 'share'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={openDetail} title="Open the detail / messages view">
                  <ExternalLink className="mr-1 h-3.5 w-3.5" /> open
                </Button>
              </>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create ticket'}
            </Button>
          </div>
        </DialogFooter>
      </form>
      <ValidationDialog
        open={validating}
        ticket={editing}
        busy={saving}
        onConfirm={handleValidate}
        onCancel={() => setValidating(false)}
      />
    </Dialog>
  );
}
